import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { LoggerService } from 'src/app/core/services/common/logger.service';
import { HydrationService } from 'src/app/core/services/login/hydration.service';
import { LoginService } from 'src/app/core/services/login/login.service';
import { LegacyUpgradeService } from 'src/app/core/services/onboarding/legacy-upgrade.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import {
  HydrationRoles,
  HydrationSources,
  IHydrationStatusStore,
} from 'src/app/core/store/common/interfaces/hydration-status-store.interface';
import { selectHydrationStatusStore } from 'src/app/core/store/common/reducers/hydration-status.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { redirectToPreDashboard } from 'src/app/core/ui-helpers/functions/redirect/redirect-to-predashboard';
import { reloadApp } from 'src/app/core/ui-helpers/functions/redirect/reload-app';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-remember-me',
  templateUrl: './remember-me.component.html',
  styleUrls: ['./remember-me.component.scss'],
})
export class RememberMeComponent implements OnDestroy, OnInit {
  headerText = 'Remember Me';
  rememberYes = '<b>Remember this device</b>';
  rememberNo = '<b>Do not remember this device</b>'; // my identity
  subscriptions: Subscription[] = [];
  isLegacy = 'false';

  constructor(
    private ls: LoginService,
    private route: ActivatedRoute,
    private router: Router,
    private lus: LegacyUpgradeService,
    private hs: HydrationService,
    private store: Store<IAppState>,
    private logger: LoggerService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        this.isLegacy = params['isLegacy'];
      }),
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async RememberMeYes() {
    await Promise.allSettled([
      firstValueFrom(this.ls.rememberMe(true)).then((res: any) => {
        if (res === false) {
          this.logger.error(
            'RememberMeComponent API call failed. Redirect anyway.',
          );
        }
      }),
      this.processDecision(),
    ]);
    return true;
  }

  async RememberMeNo() {
    await Promise.allSettled([
      firstValueFrom(this.ls.rememberMe(false)).then((res: any) => {
        if (res === false) {
          this.logger.error(
            'RememberMeComponent API call failed. Redirect anyway.',
          );
        }
      }),
      this.processDecision(),
    ]);
    return true;
  }

  async processDecision() {
    try {
      if (this.isLegacy === 'true') {
        const res = await this.redirectToLegacyUpgrade();
        if (res) return;
      }

      const hydrationStatus$ = this.store.select(selectHydrationStatusStore);
      this.subscriptions.push(
        hydrationStatus$.subscribe(async (props: IHydrationStatusStore) => {
          if (props.hydrationRole !== HydrationRoles.empty) {
            if (
              props.hydrationRole === HydrationRoles.onboardingProspect ||
              props.hydrationRole === HydrationRoles.onboardingClient ||
              props.hydrationRole ===
                HydrationRoles.onboardingProspectOrOnboardingClient
            ) {
              if (props.hydrationSource === HydrationSources.empty) {
                await this.hs.HydrateOnboardingStore(
                  props.hydrationRole,
                  HydrationSources.loginRememberMe,
                );
              }
              redirectToPreDashboard(this.router);
              return;
            } else if (
              props.hydrationRole === HydrationRoles.activeClient ||
              props.hydrationRole === HydrationRoles.internal
            ) {
              if (props.hydrationSource === HydrationSources.empty) {
                await this.hs.HydrateAccountStore(
                  props.hydrationRole,
                  HydrationSources.loginRememberMe,
                );
              }
              reloadApp(environment);
              return;
            }
          } else {
            this.logger.warn(
              'RememberMeComponent has no HydrationRole to use for navigation.',
            );
            this.ls.logout();
            return;
          }
        }),
      );
    } catch (error: any) {
      console.error(`error message: ${error.message}`);
      console.error(`error: ${JSON.stringify(error)}`);
    }
    return;
  }

  async redirectToLegacyUpgrade() {
    const confirmedLegacy = await this.lus.redirectToLegacyUpgrade();
    if (confirmedLegacy) {
      const predashboardUrl = this.router
        .createUrlTree(['OnBoarding/OnBoarding/PreDashBoard'])
        .toString();
      this.router.navigate([predashboardUrl]);
      return true;
    }
    return false;
  }
}
